"use client";

import RootComponentWrapper from "@components/RootWrapper";
import { GlobalModalProvider } from "@components/GlobalModalProvider/GlobalModalProvider";
import { ShowLoginHandler } from "@components/auth/ShowLoginHandler";
import { BloomreachLeadCaptureController } from "@components/newsletter-subscription/bloomreach/BloomreachLeadCaptureController";
import { COOKIE_INFORMATION_CONSENT_CHANGED_EVENT } from "@lib/analytics/cookieConsent";
import { getQueryClient } from "@lib/helpers/queryClient";
import { hasWindow } from "@lib/utils/hasWindow";
import { protectRouteAnnouncerFromTranslation } from "@lib/utils/protectRouteAnnouncerFromTranslation";
import { installArrayPrototypePollutionGuard } from "@lib/monitoring/arrayPrototypePollutionGuard";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@ecco/ui/theme-provider";
import { ToastContainer } from "@ecco/ui/use-toast";
import { SessionProvider } from "next-auth/react";
import { NextIntlClientProvider } from "next-intl";
import { ZustandProvider } from "../../store";
import { useEffect, useState } from "react";
import ContextDependentScripts from "@lib/scripts/ContextDependentScripts";
import { ExperimentSubjectSync } from "@lib/experiments/ExperimentSubjectSync";
import { evaluateDatadogAaValidation } from "@lib/experiments/aaValidation";
import type { FeatureFlags } from "@lib/feature-flags/types";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import dynamic from "next/dynamic";

const PreviewMode = dynamic(() => import("@components/cms/preview/PreviewMode"), {
    ssr: false,
});

interface ClientProvidersProps {
    children: React.ReactNode;
    featureFlags: FeatureFlags | null;
    isPreview: boolean;
    locale: string;
}

// Neutralize Google Pay's Array.prototype pollution before its SDK loads on the payment step.
installArrayPrototypePollutionGuard();

export default function ClientProviders({
    children,
    featureFlags,
    isPreview,
    locale,
}: ClientProvidersProps) {
    const [trackFeatureFlags, setTrackFeatureFlags] = useState<
        ((featureFlags: FeatureFlags | null) => void) | null
    >(null);

    useEffect(() => protectRouteAnnouncerFromTranslation(), []);

    useEffect(() => {
        if (!hasWindow) return;

        // Dynamically import Kount and Datadog to keep them out of the shared JS bundle.
        // Both are only ever used after hydration so there is no functional difference.
        let isUnmounted = false;
        let syncConsent: (() => void) | undefined;
        const onConsentChange = () => {
            syncConsent?.();
        };
        window.addEventListener(COOKIE_INFORMATION_CONSENT_CHANGED_EVENT, onConsentChange);

        Promise.allSettled([
            import("@lib/utils/kount/session"),
            import("@lib/monitoring/initializeDatadog"),
        ]).then(([kountResult, datadogResult]) => {
            if (isUnmounted) return;

            if (kountResult.status === "fulfilled") {
                kountResult.value.initializeKount();
            } else {
                Logger.error(ServiceType.WEB, "Failed to initialize Kount", {
                    tag: LogTag.ERROR,
                    error: kountResult.reason,
                });
            }

            if (datadogResult.status === "fulfilled") {
                const { initializeDatadog, syncDatadogConsent, trackFeatureFlagEvaluations } =
                    datadogResult.value;
                initializeDatadog();
                setTrackFeatureFlags(() => trackFeatureFlagEvaluations);

                syncConsent = syncDatadogConsent;
            } else {
                Logger.error(ServiceType.WEB, "Failed to initialize Datadog", {
                    tag: LogTag.ERROR,
                    error: datadogResult.reason,
                });
            }
        });

        return () => {
            isUnmounted = true;
            window.removeEventListener(COOKIE_INFORMATION_CONSENT_CHANGED_EVENT, onConsentChange);
        };
    }, []);

    useEffect(() => {
        trackFeatureFlags?.(featureFlags);
    }, [featureFlags, trackFeatureFlags]);

    const queryClient = getQueryClient();

    return (
        <NextIntlClientProvider locale={locale}>
            <SessionProvider>
                <QueryClientProvider client={queryClient}>
                    <ZustandProvider>
                        <RootComponentWrapper>
                            <ThemeProvider>
                                <ToastContainer />
                                <GlobalModalProvider>
                                    <ExperimentSubjectSync
                                        enabled={
                                            featureFlags?.isDatadogExperimentsEnabled?.enabled ??
                                            false
                                        }
                                        monitoringReady={Boolean(trackFeatureFlags)}
                                        onReady={evaluateDatadogAaValidation}
                                    />
                                    <ShowLoginHandler />
                                    {isPreview ? <PreviewMode preview /> : null}
                                    <BloomreachLeadCaptureController />
                                    {children}
                                </GlobalModalProvider>
                                <ReactQueryDevtools initialIsOpen={false} />
                                <ContextDependentScripts />
                            </ThemeProvider>
                        </RootComponentWrapper>
                    </ZustandProvider>
                </QueryClientProvider>
            </SessionProvider>
        </NextIntlClientProvider>
    );
}
